import React from 'react';
import { Link } from 'react-router-dom';
import { Repeat, Briefcase, HardHat, CheckCircle, AlertTriangle, AlertCircle, TrendingUp, Info, ChevronDown, ChevronUp, Target, BarChart3, X } from 'lucide-react';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine, Area } from 'recharts';
import Modal from '../UI/Modal';
import PillToggle from '../UI/PillToggle';
import { getTurnoverBadge, getTurnoverInterpretation } from '../../config/dashboardHelpers';

const TurnoverSection = ({
  turnoverData,
  turnoverLoading,
  turnoverViewMode,
  setTurnoverViewMode,
  showTurnoverInfoModal,
  setShowTurnoverInfoModal,
  showCalculationDetails,
  setShowCalculationDetails,
}) => {
  return (
    <>
      {/* Employee Turnover Rate Section */}
      <section className="space-y-6" aria-label="Employee Turnover Monitoring">
        <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
          {/* Section Header */}
          <div className="mb-4 sm:mb-6">
            <div className="flex items-center gap-2 sm:gap-3 justify-between">
              <div className="flex items-center gap-2 min-w-0">
                <Repeat className="w-5 h-5 sm:w-6 sm:h-6 text-red-600 flex-shrink-0" />
                <h2 className="text-lg sm:text-2xl font-bold text-gray-900 min-w-0 truncate">Employee Turnover Rate</h2>
                <button
                  onClick={() => setShowTurnoverInfoModal(true)}
                  className="icon-button-admin icon-button-sm flex-shrink-0"
                  aria-label="View turnover rate calculation details"
                  title="How is this calculated?"
                >
                  <Info className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 hover:text-blue-600" />
                </button>
              </div>
              <PillToggle
                value={turnoverViewMode}
                onChange={setTurnoverViewMode}
                options={[
                  { id: 'ttm', label: 'TTM', title: 'Trailing 12-Month View' },
                  { id: 'yoy', label: 'YoY', title: 'Year-on-Year View' },
                ]}
                variant="compact"
                theme="admin"
                className="flex-shrink-0"
                ariaLabel="Turnover view mode"
              />
            </div>
            <p className="text-xs sm:text-sm text-gray-600 mt-1 ml-7 sm:ml-8">
              {turnoverViewMode === 'ttm' 
                ? 'Rolling 12-Month • Singapore Construction: ~12-18% (MOM)'
                : 'Year-on-Year Comparison • Same Month Across Years'
              }
            </p>
          </div>

          {turnoverLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
              <span className="ml-3 text-gray-600">Loading turnover data...</span>
            </div>
          ) : turnoverData ? (
            <>
              {/* TTM View */}
              {turnoverViewMode === 'ttm' && (
              <>
              {/* Top metrics: Overall spans the phone/tablet row so the YoY
                  badge stays readable; Staff + Worker sit side-by-side for
                  comparison. From lg up the three share a single row. */}
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6 mb-4 sm:mb-6">
                {/* Overall */}
                <div className="col-span-2 lg:col-span-1 bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center min-w-0 flex-1">
                      <div className="w-9 h-9 sm:w-12 sm:h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <Repeat className="w-4 h-4 sm:w-6 sm:h-6 text-red-600" />
                      </div>
                      <div className="ml-2 sm:ml-4 min-w-0">
                        <p className="text-lg sm:text-2xl font-bold text-gray-900 tabular-nums truncate">
                          {turnoverData.rolling_12_month.turnover_rate.overall.toFixed(1)}%
                        </p>
                        <p className="text-xs sm:text-sm text-gray-600 truncate">Overall Turnover</p>
                      </div>
                    </div>
                    <div className={`flex items-center rounded-full px-2 py-1 text-xs font-medium flex-shrink-0 whitespace-nowrap ${
                      turnoverData.yoy_change > 0 ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                    }`}>
                      {turnoverData.yoy_change > 0 ? '↑' : '↓'} {Math.abs(turnoverData.yoy_change)}%<span className="hidden sm:inline">&nbsp;vs LY</span>
                    </div>
                  </div>
                </div>
                
                {/* Staff */}
                <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
                  <div className="flex items-start min-w-0">
                    <div className="w-9 h-9 sm:w-12 sm:h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Briefcase className="w-4 h-4 sm:w-6 sm:h-6 text-blue-800" />
                    </div>
                    <div className="ml-2 sm:ml-4 min-w-0">
                      <p className="text-lg sm:text-2xl font-bold text-gray-900 tabular-nums truncate">
                        {turnoverData.rolling_12_month.turnover_rate.staff.toFixed(1)}%
                      </p>
                      <p className="text-xs sm:text-sm text-gray-600 truncate">
                        <span className="sm:hidden">Staff</span>
                        <span className="hidden sm:inline">Staff Turnover</span>
                      </p>
                      {(() => {
                        const badge = getTurnoverBadge('staff', turnoverData.rolling_12_month.turnover_rate.staff);
                        const Icon = badge.level === 'healthy' ? CheckCircle : badge.level === 'monitor' ? AlertTriangle : AlertCircle;
                        return (
                          <div className={`inline-flex items-center rounded-full px-2 py-0.5 mt-1 text-xs font-medium ${badge.bg} ${badge.text}`}>
                            <Icon className="w-3 h-3 mr-1 flex-shrink-0" />
                            {badge.label}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </div>
                
                {/* Workers */}
                <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
                  <div className="flex items-start min-w-0">
                    <div className="w-9 h-9 sm:w-12 sm:h-12 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <HardHat className="w-4 h-4 sm:w-6 sm:h-6 text-amber-600" />
                    </div>
                    <div className="ml-2 sm:ml-4 min-w-0">
                      <p className="text-lg sm:text-2xl font-bold text-gray-900 tabular-nums truncate">
                        {turnoverData.rolling_12_month.turnover_rate.worker.toFixed(1)}%
                      </p>
                      <p className="text-xs sm:text-sm text-gray-600 truncate">
                        <span className="sm:hidden">Workers</span>
                        <span className="hidden sm:inline">Worker Turnover</span>
                      </p>
                      {(() => {
                        const badge = getTurnoverBadge('worker', turnoverData.rolling_12_month.turnover_rate.worker);
                        const Icon = badge.level === 'healthy' ? CheckCircle : badge.level === 'monitor' ? AlertTriangle : AlertCircle;
                        return (
                          <div className={`inline-flex items-center rounded-full px-2 py-0.5 mt-1 text-xs font-medium ${badge.bg} ${badge.text}`}>
                            <Icon className="w-3 h-3 mr-1 flex-shrink-0" />
                            {badge.label}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </div>

              {/* Chart Section */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">12-Month Trend Analysis</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={turnoverData.trend_data} margin={{ top: 5, right: 10, left: -15, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis 
                      dataKey="month" 
                      stroke="#6b7280"
                      tick={{ fontSize: 10 }}
                      interval="preserveStartEnd"
                    />
                    <YAxis 
                      stroke="#6b7280"
                      tick={{ fontSize: 12 }}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'white', 
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        padding: '8px'
                      }}
                    />
                    <Legend 
                      wrapperStyle={{ paddingTop: '20px' }}
                      iconType="line"
                    />
                    
                    {/* Industry benchmark */}
                    <ReferenceLine 
                      y={turnoverData.industry_benchmark} 
                      stroke="#fbbf24" 
                      strokeDasharray="5 5"
                      label={{ 
                        value: `Industry Avg (${turnoverData.industry_benchmark}%)`, 
                        position: 'right', 
                        fill: '#92400e' 
                      }}
                    />
                    
                    <defs>
                      <linearGradient id="staffFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.15} />
                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="workerFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.15} />
                        <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    
                    <Area type="monotone" dataKey="staff" stroke="none" fill="url(#staffFill)" />
                    <Area type="monotone" dataKey="worker" stroke="none" fill="url(#workerFill)" />
                    
                    <Line 
                      type="monotone" 
                      dataKey="staff" 
                      stroke="#3b82f6" 
                      strokeWidth={2}
                      name="Staff Turnover"
                      dot={{ r: 3 }}
                      activeDot={{ r: 5 }}
                    />
                    
                    <Line 
                      type="monotone" 
                      dataKey="worker" 
                      stroke="#f59e0b" 
                      strokeWidth={2}
                      name="Worker Turnover"
                      dot={{ r: 3 }}
                      activeDot={{ r: 5 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* Insights Section */}
              <div className="border-t pt-6">
                <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                  <Info className="w-4 h-4 mr-2 text-blue-600" />
                  Key Insights
                </h3>
                
                <div className="space-y-2">
                  {/* Staff Insight - Dynamic */}
                  {(() => {
                    const staffRate = turnoverData.rolling_12_month.turnover_rate.staff;
                    const insight = getTurnoverInterpretation('staff', staffRate);
                    const Icon = insight.level === 'healthy' ? CheckCircle : insight.level === 'monitor' ? AlertTriangle : AlertCircle;
                    return (
                      <div className="flex items-start space-x-2">
                        <Icon className={`w-4 h-4 ${insight.color} flex-shrink-0 mt-0.5`} />
                        <p className="text-sm text-gray-700">
                          <span className="font-medium">{insight.message}</span> {insight.detail}
                        </p>
                      </div>
                    );
                  })()}
                  
                  {/* Worker Insight - Dynamic */}
                  {(() => {
                    const workerRate = turnoverData.rolling_12_month.turnover_rate.worker;
                    const insight = getTurnoverInterpretation('worker', workerRate);
                    const Icon = insight.level === 'healthy' ? CheckCircle : insight.level === 'monitor' ? AlertTriangle : AlertCircle;
                    return (
                      <div className="flex items-start space-x-2">
                        <Icon className={`w-4 h-4 ${insight.color} flex-shrink-0 mt-0.5`} />
                        <p className="text-sm text-gray-700">
                          <span className="font-medium">{insight.message}</span> {insight.detail}
                        </p>
                      </div>
                    );
                  })()}
                  
                  {/* Overall Trend */}
                  <div className="flex items-start space-x-2">
                    <TrendingUp className="w-4 h-4 text-orange-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-gray-700">
                      <span className="font-medium">Overall trend:</span>{' '}
                      {turnoverData.yoy_change > 0 ? '+' : ''}{turnoverData.yoy_change}% change vs last year — 
                      {Math.abs(turnoverData.yoy_change) > 2 ? ' monitor closely' : ' stable performance'}
                    </p>
                  </div>
                </div>
              </div>
              </>
              )}

              {/* YoY View */}
              {turnoverViewMode === 'yoy' && turnoverData.yoy_data && (
              <>
                {/* YoY Chart Section */}
                <div className="bg-white rounded-lg border p-6">
                  {/* Chart Header */}
                  <div className="mb-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4">
                      <div className="min-w-0">
                        <h4 className="text-base sm:text-lg font-semibold text-gray-900">Year-on-Year Turnover Comparison</h4>
                        <p className="text-xs sm:text-sm text-gray-600">
                          Same month across multiple years • {turnoverData.yoy_comparison?.comparison_month || 'Latest'}: <span className="font-semibold text-orange-600">{turnoverData.yoy_comparison?.current_year?.rate || '0'}%</span>
                        </p>
                      </div>
                      <div className="flex items-center gap-3 sm:gap-4 flex-shrink-0">
                        <div className="flex items-center gap-1.5">
                          <div className="w-3 h-0.5 bg-red-500"></div>
                          <span className="text-[11px] sm:text-xs text-gray-600">{Object.keys(turnoverData.yoy_data).find(k => k.startsWith('y'))?.substring(1) || new Date().getFullYear()} (Current)</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <div className="w-3 h-0.5 bg-green-500"></div>
                          <span className="text-[11px] sm:text-xs text-gray-600">{Object.keys(turnoverData.yoy_data).filter(k => k.startsWith('y'))[1]?.substring(1) || new Date().getFullYear() - 1}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <div className="w-3 h-0.5 bg-purple-500"></div>
                          <span className="text-[11px] sm:text-xs text-gray-600">{Object.keys(turnoverData.yoy_data).filter(k => k.startsWith('y'))[2]?.substring(1) || new Date().getFullYear() - 2}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* YoY Line Chart */}
                  <div className="relative">
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart 
                          data={turnoverData.yoy_data.months.map((month, idx) => ({
                            month,
                            ...Object.keys(turnoverData.yoy_data)
                              .filter(k => k.startsWith('y'))
                              .reduce((acc, yearKey) => ({
                                ...acc,
                                [yearKey]: turnoverData.yoy_data[yearKey][idx]
                              }), {})
                          }))}
                          margin={{ top: 10, right: 10, left: -15, bottom: 0 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="month" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                          <YAxis tick={{ fontSize: 11 }} />
                          <Tooltip formatter={(v) => [`${v}%`, 'Turnover Rate']} />
                          <Line 
                            type="monotone" 
                            dataKey={Object.keys(turnoverData.yoy_data).find(k => k.startsWith('y'))} 
                            name={`${Object.keys(turnoverData.yoy_data).find(k => k.startsWith('y'))?.substring(1)} (Current)`}
                            stroke="#EF4444" 
                            strokeWidth={2} 
                            strokeDasharray="4 4" 
                            dot={{ r: 3 }} 
                          />
                          <Line 
                            type="monotone" 
                            dataKey={Object.keys(turnoverData.yoy_data).filter(k => k.startsWith('y'))[1]} 
                            name={Object.keys(turnoverData.yoy_data).filter(k => k.startsWith('y'))[1]?.substring(1)}
                            stroke="#10B981" 
                            strokeWidth={1.5} 
                            dot={false} 
                          />
                          <Line 
                            type="monotone" 
                            dataKey={Object.keys(turnoverData.yoy_data).filter(k => k.startsWith('y'))[2]} 
                            name={Object.keys(turnoverData.yoy_data).filter(k => k.startsWith('y'))[2]?.substring(1)}
                            stroke="#8B5CF6" 
                            strokeWidth={1.5} 
                            dot={false} 
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* Same-month YoY snapshot — secondary to the chart, keep dense */}
                  {turnoverData.yoy_comparison && (
                    <div className="mt-3 sm:mt-4 rounded-lg bg-gray-50 border border-gray-100 px-3 py-2.5 sm:px-4 sm:py-3">
                      <p className="text-[11px] sm:text-xs text-gray-500 mb-2">
                        {turnoverData.yoy_comparison.comparison_month} comparison across years
                      </p>
                      <div className="grid grid-cols-3 gap-2 sm:gap-0 sm:divide-x sm:divide-gray-200">
                        {/* Current Year */}
                        <div className="text-center sm:px-3">
                          <div className="flex items-center justify-center gap-1.5 mb-0.5">
                            <div className="w-2 h-2 bg-red-600 rounded-full flex-shrink-0" aria-hidden="true" />
                            <p className="text-[11px] sm:text-xs font-medium text-gray-600 truncate">
                              {turnoverData.yoy_comparison.comparison_month} {turnoverData.yoy_comparison.current_year.year}
                            </p>
                          </div>
                          <p className="text-base sm:text-lg font-bold text-red-600 leading-tight">
                            {turnoverData.yoy_comparison.current_year.rate}%
                          </p>
                          <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5">Current</p>
                        </div>

                        {/* Last Year */}
                        <div className="text-center sm:px-3">
                          <div className="flex items-center justify-center gap-1.5 mb-0.5">
                            <div className="w-2 h-2 bg-green-500 rounded-full flex-shrink-0" aria-hidden="true" />
                            <p className="text-[11px] sm:text-xs font-medium text-gray-600 truncate">
                              {turnoverData.yoy_comparison.comparison_month} {turnoverData.yoy_comparison.last_year.year}
                            </p>
                          </div>
                          <p className="text-base sm:text-lg font-bold text-green-600 leading-tight">
                            {turnoverData.yoy_comparison.last_year.rate}%
                          </p>
                          <p className={`text-[10px] sm:text-xs font-medium mt-0.5 ${
                            turnoverData.yoy_comparison.last_year.change_pct > 0 ? 'text-red-700' : 'text-green-700'
                          }`}>
                            {turnoverData.yoy_comparison.last_year.change_pct > 0 ? '+' : ''}
                            {turnoverData.yoy_comparison.last_year.change_pct}% vs current
                          </p>
                        </div>

                        {/* Two Years Ago */}
                        <div className="text-center sm:px-3">
                          <div className="flex items-center justify-center gap-1.5 mb-0.5">
                            <div className="w-2 h-2 bg-purple-500 rounded-full flex-shrink-0" aria-hidden="true" />
                            <p className="text-[11px] sm:text-xs font-medium text-gray-600 truncate">
                              {turnoverData.yoy_comparison.comparison_month} {turnoverData.yoy_comparison.two_years_ago.year}
                            </p>
                          </div>
                          <p className="text-base sm:text-lg font-bold text-purple-600 leading-tight">
                            {turnoverData.yoy_comparison.two_years_ago.rate}%
                          </p>
                          <p className={`text-[10px] sm:text-xs font-medium mt-0.5 ${
                            turnoverData.yoy_comparison.two_years_ago.change_pct > 0 ? 'text-red-700' : 'text-green-700'
                          }`}>
                            {turnoverData.yoy_comparison.two_years_ago.change_pct > 0 ? '+' : ''}
                            {turnoverData.yoy_comparison.two_years_ago.change_pct}% vs current
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </>
              )}
            </>
          ) : (
            <div className="text-center py-12">
              <Repeat className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600">No turnover data available</p>
              <p className="text-sm text-gray-500 mt-2">Historical data will be generated automatically</p>
            </div>
          )}
        </div>
      </section>

      {/* Turnover Info Modal */}
      {showTurnoverInfoModal && (
        <Modal
          isOpen
          onClose={() => setShowTurnoverInfoModal(false)}
          size="lg"
          theme="admin"
          className="max-w-3xl"
          contentClassName="space-y-3"
          customHeader={(
            <div className="modal-header">
              <div className="min-w-0 flex-1">
                <h2 className="text-base sm:text-2xl font-bold text-gray-900 flex items-center gap-2 min-w-0">
                  <Repeat className="w-5 h-5 sm:w-6 sm:h-6 text-red-600 flex-shrink-0" />
                  <span className="truncate">Understanding Turnover Rate</span>
                </h2>
                <p className="text-xs sm:text-sm text-gray-600 mt-1 truncate">How we calculate and why it matters</p>
              </div>
              <button
                onClick={() => setShowTurnoverInfoModal(false)}
                className="icon-button-admin flex-shrink-0"
                aria-label="Close modal"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
          footer={(
            <button
              onClick={() => setShowTurnoverInfoModal(false)}
              className="btn btn-admin-primary"
            >
              Close
            </button>
          )}
        >
              
              {/* What is Turnover Rate + Formula (Merged) */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center">
                    <Info className="w-5 h-5 mr-2 text-blue-700" />
                    <h3 className="text-base font-semibold text-blue-900">Turnover Rate Calculation</h3>
                  </div>
                </div>
                <p className="text-sm text-blue-800 mb-3">
                  Turnover rate measures the percentage of employees who left your organization during a specific period.
                </p>
                <div className="bg-white border-2 border-blue-300 rounded-lg px-4 py-2 mb-2">
                  <p className="text-xs text-gray-600 mb-1">Formula</p>
                  <p className="text-lg font-bold text-gray-900">
                    (Employees who left ÷ Average employees) × 100
                  </p>
                </div>
                
                {/* Collapsible Calculation Details */}
                <button
                  onClick={() => setShowCalculationDetails(!showCalculationDetails)}
                  className="flex items-center text-sm text-blue-700 hover:text-blue-800 font-medium"
                >
                  {showCalculationDetails ? (
                    <>
                      <ChevronUp className="w-4 h-4 mr-1" />
                      Hide calculation details
                    </>
                  ) : (
                    <>
                      <ChevronDown className="w-4 h-4 mr-1" />
                      Show calculation details
                    </>
                  )}
                </button>
                
                {showCalculationDetails && (
                  <div className="mt-3 pt-3 border-t border-blue-200 space-y-2 text-sm">
                    <div>
                      <p className="font-semibold text-gray-900 mb-1">1. Count employees who left</p>
                      <p className="text-gray-700 text-xs">
                        Any employee whose employment end date falls within the period.
                      </p>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 mb-1">2. Calculate average employees</p>
                      <p className="text-gray-700 text-xs mb-1">
                        For each month: <span className="font-mono text-purple-600">(Start headcount + End headcount) ÷ 2</span>
                      </p>
                      <p className="text-xs text-gray-500">
                        For 12-month period, we average all monthly averages together.
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Real Example with Your Data */}
              {turnoverData && (
                <div>
                  <h3 className="text-base font-semibold text-gray-900 mb-2 flex items-center">
                    <BarChart3 className="w-5 h-5 mr-2 text-green-600" />
                    Example with Your Actual Data
                  </h3>
                  
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-3">
                    <p className="text-sm text-gray-700 mb-3">
                      <span className="font-semibold">Period:</span> {turnoverData.rolling_12_month.period}
                    </p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                      <div className="bg-white rounded-lg p-3 border border-green-200">
                        <p className="text-xs text-gray-600 mb-1">Employees who left</p>
                        <p className="text-2xl font-bold text-gray-900">
                          {turnoverData.rolling_12_month.separations.total}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {turnoverData.rolling_12_month.separations.staff} staff, {turnoverData.rolling_12_month.separations.worker} workers
                        </p>
                      </div>
                      
                      <div className="bg-white rounded-lg p-3 border border-green-200">
                        <p className="text-xs text-gray-600 mb-1">Average employees</p>
                        <p className="text-2xl font-bold text-gray-900">
                          {turnoverData.rolling_12_month.avg_headcount.total}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          Over 12 months
                        </p>
                      </div>
                      
                      <div className="bg-white rounded-lg p-3 border border-green-200">
                        <p className="text-xs text-gray-600 mb-1">Turnover Rate</p>
                        <p className="text-2xl font-bold text-green-600">
                          {turnoverData.rolling_12_month.turnover_rate.overall}%
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          Overall company rate
                        </p>
                      </div>
                    </div>
                    
                    <div className="bg-white border border-green-300 rounded-lg p-3">
                      <p className="text-sm font-mono text-gray-700">
                        ({turnoverData.rolling_12_month.separations.total} ÷ {turnoverData.rolling_12_month.avg_headcount.total}) × 100 = <span className="font-bold text-green-600">{turnoverData.rolling_12_month.turnover_rate.overall}%</span>
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* What's a Healthy Rate */}
              <div>
                <h3 className="text-base font-semibold text-gray-900 mb-2 flex items-center">
                  <Target className="w-5 h-5 mr-2 text-blue-600" />
                  What's a Healthy Rate?
                </h3>
                
                <div className="space-y-2">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-semibold text-green-900">Staff (Office/Management)</span>
                      <span className="text-base font-bold text-green-700">10-15%</span>
                    </div>
                    <p className="text-xs text-green-700">Healthy range for staff positions</p>
                  </div>
                  
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-semibold text-yellow-900">Workers (Construction)</span>
                      <span className="text-base font-bold text-yellow-700">20-30%</span>
                    </div>
                    <p className="text-xs text-yellow-700">Industry benchmark for construction sector</p>
                  </div>
                  
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-semibold text-blue-900">Overall Company</span>
                      <span className="text-base font-bold text-blue-700">15-25%</span>
                    </div>
                    <p className="text-xs text-blue-700">Typical range for construction companies</p>
                  </div>
                </div>
              </div>

              {/* Why It Matters */}
              <div>
                <h3 className="text-base font-semibold text-gray-900 mb-2 flex items-center">
                  <AlertTriangle className="w-5 h-5 mr-2 text-orange-600" />
                  Why Does It Matter?
                </h3>
                
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start">
                      <span className="text-orange-600 mr-2">•</span>
                      <span><span className="font-semibold">Financial Impact:</span> Recruiting and training costs 50-200% of annual salary, plus productivity loss while new hires ramp up</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-orange-600 mr-2">•</span>
                      <span><span className="font-semibold">Team & Knowledge:</span> High turnover affects morale and leads to loss of experienced skills and institutional knowledge</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-orange-600 mr-2">•</span>
                      <span><span className="font-semibold">Client Impact:</span> In construction, worker continuity directly affects project quality and client satisfaction</span>
                    </li>
                  </ul>
                </div>
              </div>
        </Modal>
      )}
    </>
  );
};

export default TurnoverSection;
